import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  LoopOnce,
  LoopRepeat,
  Object3D,
} from 'three';

import { FrameRate } from '@cartwhl/common/types';

import { createFrameMixerController } from './frameMixerController';
import { useSyncedFrameMixers } from './useSyncedFrameMixers';

/**
 * @typedef {Object} Config
 * @property {FrameRate} [frameRate] - The frame rate of the animation (default: 24).
 * @property {number} [playbackSpeed] - The playback speed of the animation (default: 1).
 * @property {boolean} [loop] - Whether the animation should loop (default: true).
 * @property {boolean} [autoplay] - Whether the animation should autoplay (default: true).
 * @property {boolean} [disabled] - Whether the mixer should be disabled (default: false).
 * @property {function} [onReady] - Callback when the animation is ready.
 * @property {function} [onFrameUpdate] - Callback on frame update.
 * @property {function} [onLoopFinish] - Callback when the loop finishes.
 * @property {function} [onPlayChange] - Callback when the play state changes.
 * @property {ReturnType<typeof useSyncedFrameMixers>} [syncController] - Used for syncing multiple mixers, will turn off autoplay.
 */
type Config = {
  frameRate?: FrameRate;
  playbackSpeed?: number;
  frameStepSize?: number;
  loop?: boolean;
  autoplay?: boolean;
  disabled?: boolean;
  onReady?: (duration: { seconds: number; frames: number }) => void;
  onFrameUpdate?: (frame: number) => void;
  onLoopFinish?: () => void;
  onPlayChange?: (isPlaying: boolean) => void;
  syncController?: ReturnType<typeof useSyncedFrameMixers>;
};

const DEFAULT_DURATION = { seconds: 0, frames: 0 };

/**
 * Custom hook to manage an animation mixer.
 *
 * @param {AnimationClip} clip - The animation clip to be played.
 * @param {Object3D<THREE.Event>} root - The root object of the animation.
 * @param {Config} [config] - Configuration options for the mixer.
 * @returns {Object} An object containing the mixer controls and status.
 * @returns {string} return.id - Unique identifier for the mixer instance.
 * @returns {function} return.update - Function to update the mixer.
 * @returns {function} return.play - Function to play the animation.
 * @returns {function} return.setFrame - Function to set the current frame of the animation.
 * @returns {function} return.getFrame - Function to get the current frame of the animation.
 * @returns {Object} return.duration - The duration of the animation in seconds and frames.
 * @returns {function} return.getPlayStatus - Function to get the play status of the animation.
 */
export default function useFrameMixer(
  clip?: AnimationClip,
  root?: Object3D<THREE.Event>,
  {
    frameRate = 24,
    playbackSpeed = 1,
    frameStepSize = 1,
    loop = true,
    autoplay = true,
    disabled = false,
    onReady,
    onFrameUpdate,
    onLoopFinish,
    onPlayChange,
    syncController,
  }: Config = {},
) {
  const id = useRef<string>(nanoid());
  const mixer = useRef<AnimationMixer>(new AnimationMixer(new Object3D()));
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const durationRef = useRef(duration);

  const previousPlayStatus = useRef<boolean | undefined>(undefined);
  const activeAction = useRef<AnimationAction | null>(null);

  durationRef.current = duration;

  const controller = useMemo(
    () =>
      createFrameMixerController({
        clip,
        mixerRef: mixer,
        activeActionRef: activeAction,
        frameRate,
        playbackSpeed,
        frameStepSize,
        loop,
        disabled,
        durationRef,
        onFrameUpdate,
        onPlayChange,
      }),
    [
      clip,
      frameRate,
      playbackSpeed,
      frameStepSize,
      loop,
      disabled,
      onFrameUpdate,
      onPlayChange,
    ],
  );

  const updateMixer = () => {
    if (!root || !clip) {
      activeAction.current?.stop();
      activeAction.current = null;
      setDuration((currentDuration) =>
        currentDuration.seconds === 0 && currentDuration.frames === 0
          ? currentDuration
          : DEFAULT_DURATION,
      );
      return;
    }

    // Remember the play state, so we can restore it after updating the mixer
    if (mixer.current) {
      previousPlayStatus.current = controller.getPlayStatus();
    }

    // Preserve the action's local clip time so changing loop mode does not
    // jump to the start or immediately clamp to the end.
    const preservedTime = activeAction.current?.time ?? 0;

    const action = mixer.current.clipAction(clip, root);
    action.setLoop(loop ? LoopRepeat : LoopOnce, Infinity);

    if (loop === false || syncController) {
      action.clampWhenFinished = true;
    }

    // We start the animation paused (Otherwise the character shows up in a T-pose)
    mixer.current.timeScale = 1;

    if (activeAction.current) {
      // NOTE: Spitting the mixer reset to a different useEffect
      // there is no longer any popping, so there is no need to
      // use crossFade. Leave it here incase we need crossFade
      // in the future.
      // if (crossFade) {
      //   console.log('CrossFade');
      //   activeAction.current.setEffectiveWeight(1);
      //   activeAction.current.setEffectiveTimeScale(1);
      //   activeAction.current.enabled = true;
      //   activeAction.current.clampWhenFinished = true;

      //   action.setEffectiveWeight(1);
      //   action.setEffectiveTimeScale(1);
      //   action.time = 0;
      //   action.enabled = true;
      //   action.clampWhenFinished = true;

      //   activeAction.current.crossFadeTo(action, 1, false);
      // } else {

      // need to stop previous action so the next action can play correctly
      activeAction.current.stop();

      // }
    }

    action.play();
    action.time = preservedTime;
    mixer.current.setTime(preservedTime); // Setting the time here prevents it from showing a t-pose

    // Restore the play state from previous mixer instance
    mixer.current.timeScale = previousPlayStatus.current ? 1 : 0;

    // If autoplay is enabled, start the animation
    // Unless a syncController is provided
    // Or unless we have a previous play status
    if (
      autoplay &&
      !syncController &&
      previousPlayStatus.current === undefined
    ) {
      mixer.current.timeScale = 1;
      action.play();
    }

    if (onPlayChange) {
      onPlayChange(mixer.current.timeScale > 0);
    }

    const seconds = clip.duration;
    const frames = Math.ceil(seconds * frameRate);
    const myDuration = { seconds, frames };
    setDuration(myDuration);

    if (syncController) {
      syncController.register(id.current, {
        play: controller.play,
        setFrame: controller.setFrame,
        duration: myDuration,
        getPlayStatus: controller.getPlayStatus,
      });
    }

    if (onReady) {
      onReady(myDuration);
    }

    const handleLoopFinish = () => {
      if (onLoopFinish) {
        onLoopFinish();
      }
      if (syncController) {
        syncController?.onLoopFinish(id.current);
      }
    };

    mixer.current.addEventListener('loop', handleLoopFinish);
    activeAction.current = action;

    return () => {
      mixer.current.removeEventListener('loop', handleLoopFinish);
      if (onPlayChange) {
        onPlayChange(false);
      }
    };
  };

  // initialize the mixer
  useEffect(() => {
    return () => {
      mixer.current.stopAllAction();
      if (root) {
        mixer.current.uncacheRoot(root);
      }
      activeAction.current = null;
    };
  }, [root]);

  // initialize the clip
  // eslint-disabled-next-line react-hooks/exhaustive-deps
  useEffect(updateMixer, [
    root,
    clip,
    loop,
    disabled,
    autoplay,
    frameRate,
    syncController,
    frameStepSize,
  ]);

  return useMemo(
    () => ({
      id: id.current,
      update: controller.update,
      play: controller.play,
      setFrame: controller.setFrame,
      getFrame: controller.getFrame,
      duration,
      getPlayStatus: controller.getPlayStatus,
    }),
    [controller, duration],
  );
}
